import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthService, WHITELISTED_AUTH_PATHS } from '@services/auth.service';

// error-codes.enum.ts
export enum ErrorCodes {
  Unauthorized = 401,
  BadRequest = 400,
  NotFound = 404,
  UnprocessableEntity = 422,
}

type ErrorHandler = (error: HttpErrorResponse) => Observable<never>;
type ErrorOptions = { [key in ErrorCodes]?: ErrorHandler };

@Injectable()
export class ErrorHandlerInterceptor implements HttpInterceptor {
  constructor(private authService: AuthService) {}

  private errorHandlers: ErrorOptions = {
    [ErrorCodes.Unauthorized]: (error: HttpErrorResponse) => this.handle401Error(error),
    [ErrorCodes.BadRequest]: (error: HttpErrorResponse) => this.handle400Error(error),
    [ErrorCodes.NotFound]: (error: HttpErrorResponse) => this.handle404Error(error),
    [ErrorCodes.UnprocessableEntity]: (error: HttpErrorResponse) => this.handle422Error(error),
  };

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(catchError((error: HttpErrorResponse) => this.handleError(error)));
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    const handler = this.errorHandlers[error.status as ErrorCodes];
    return handler ? handler(error) : throwError(() => error);
  }

  private handle401Error(error: HttpErrorResponse): Observable<never> {
    const isOAuthFlow = !!error.url?.includes('/oauth/token') || !!error.url?.includes('/logout');
    const isWhitelistedPath = WHITELISTED_AUTH_PATHS.some(path => window.location.href.includes(path));
    if (!isWhitelistedPath && !isOAuthFlow && !this.authService.isLoggingOut) {
      this.authService.logout(true, false, true, true);
    }
    return throwError(() => error);
  }

  private handle400Error(error: HttpErrorResponse): Observable<never> {
    if (!error.url?.includes('/oauth/token') && !error.url?.includes('/logout')) {
      if (error.error?.error === 'invalid_grant' && !this.authService.isLoggingOut) {
        this.authService.logout(true, false, true, true);
      }
    }
    return throwError(() => error);
  }

  private handle404Error(error: HttpErrorResponse): Observable<never> {
    return throwError(() => error);
  }

  private handle422Error(error: HttpErrorResponse): Observable<never> {
    return throwError(() => error);
  }
}
